import { useState } from "react";
import { Landmark, Send, Ban, FileCode2, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mydataApi, mydataError } from "@/api";
import { useLang, fmtDate } from "@/i18n";
import { MyDataBadge } from "@/components/MyDataBadge";

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 text-sm"><span className="text-slate-500">{label}</span><span className="font-mono" data-testid={testId}>{value || "—"}</span></div>
);

export const MyDataPanel = ({ doc, onChange }) => {
  const { t, lang } = useLang();
  const md = doc.mydata;
  const isSale = ["invoice", "service_invoice", "retail_receipt"].includes(doc.type);
  const [invoiceType, setInvoiceType] = useState({ service_invoice: "2.1", retail_receipt: "11.1" }[doc.type] || "1.1");
  const [mark, setMark] = useState(md?.supplier_mark || "");
  const [category, setCategory] = useState(md?.category || "goods");
  const [busy, setBusy] = useState(false);
  const sentOk = md?.status === "sent" || md?.status === "mock";

  const run = async (fn, okKey) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.mydata.status === "error") toast.error(`${t("mydata_error")}: ${res.mydata.errors?.[0]?.message || ""}`);
      else toast.success(t(okKey));
      onChange({ ...doc, mydata: res.mydata });
    } catch (e) { toast.error(mydataError(e, t)); } finally { setBusy(false); }
  };

  return (
    <div className="erp-card no-print" data-testid="mydata-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <Landmark size={18} className="text-blue-700" />
          <h3 className="font-heading font-semibold">{t("nav_mydata")}</h3>
          <MyDataBadge mydata={md} />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {isSale && !sentOk && (
            <>
              <Select value={invoiceType} onValueChange={setInvoiceType}>
                <SelectTrigger className="w-64 h-9" data-testid="mydata-invoice-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1.1">{t("type_1_1")}</SelectItem>
                  <SelectItem value="2.1">{t("type_2_1")}</SelectItem>
                  <SelectItem value="11.1">{t("type_11_1")}</SelectItem>
                  <SelectItem value="11.2">{t("type_11_2")}</SelectItem>
                </SelectContent>
              </Select>
              <a href={mydataApi.xmlUrl(doc.id, invoiceType)} target="_blank" rel="noreferrer" data-testid="mydata-view-xml-link"
                className="h-9 px-3 inline-flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-100 dark:hover:bg-slate-800">
                <FileCode2 size={14} />{t("view_xml")}
              </a>
              <Button size="sm" disabled={busy || !["issued", "paid"].includes(doc.status)} onClick={() => run(() => mydataApi.send(doc.id, invoiceType), "mydata_sent_ok")}
                className="bg-blue-800 hover:bg-blue-700" data-testid="mydata-send-button"><Send size={14} className="mr-1" />{t("mydata_send")}</Button>
            </>
          )}
          {isSale && sentOk && (
            <Button size="sm" variant="outline" className="text-rose-600" disabled={busy} onClick={() => run(() => mydataApi.cancel(doc.id), "mydata_cancelled_ok")} data-testid="mydata-cancel-button">
              <Ban size={14} className="mr-1" />{t("mydata_cancel")}
            </Button>
          )}
          {!isSale && !sentOk && (
            <>
              <Input value={mark} onChange={(e) => setMark(e.target.value)} placeholder={t("mydata_supplier_mark")} className="w-56 h-9 font-mono" data-testid="mydata-supplier-mark-input" />
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-64 h-9" data-testid="mydata-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="goods">{t("cat_goods")}</SelectItem>
                  <SelectItem value="services">{t("cat_services")}</SelectItem>
                  <SelectItem value="expenses">{t("cat_expenses")}</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" disabled={busy || mark.trim().length !== 15} onClick={() => run(() => mydataApi.classify(doc.id, { supplier_mark: mark, category }), "mydata_classified_ok")}
                className="bg-blue-800 hover:bg-blue-700" data-testid="mydata-classify-button"><Tag size={14} className="mr-1" />{t("mydata_classify")}</Button>
            </>
          )}
        </div>
      </div>
      {md && (
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Row label={t("mark")} value={md.mark} testId="mydata-mark" />
          <Row label={t("sent_at")} value={md.sent_at ? fmtDate(md.sent_at, lang) : null} />
          {isSale && <Row label={t("mydata_invoice_type")} value={md.invoice_type} />}
          {isSale && md.cancellation_mark && <Row label={`${t("mark")} ${t("md_status_cancelled")}`} value={md.cancellation_mark} testId="mydata-cancellation-mark" />}
          {!isSale && <Row label={t("mydata_supplier_mark")} value={md.supplier_mark} />}
          {!isSale && md.category && <Row label={t("mydata_category")} value={t(`cat_${md.category}`)} />}
          {md.uid && <Row label={t("uid")} value={md.uid} />}
          {md.errors?.length > 0 && (
            <div className="sm:col-span-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded p-3 font-mono" data-testid="mydata-errors">
              {md.errors.map((e, i) => <div key={i}>{e.code ? `[${e.code}] ` : ""}{e.message}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
